import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SuperAdminMetrics, Workspace, Broadcast, ImpersonateTokenResponse } from '@/types/superadmin';

export function useSuperAdminMetrics(enabled: boolean = true) {
  return useQuery<SuperAdminMetrics>({
    queryKey: ['superadmin', 'metrics'],
    queryFn: async () => {
      const { data } = await api.get('/superadmin/metrics');
      return data;
    },
    enabled,
    refetchInterval: enabled ? 30000 : false,
  });
}

export function useSuperAdminWorkspaces(
  status?: string,
  search?: string,
  subscription_status?: string,
  enabled: boolean = true
) {
  return useQuery<Workspace[]>({
    queryKey: ['superadmin', 'workspaces', { status, search, subscription_status }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      if (search) params.search = search;
      if (subscription_status) params.subscription_status = subscription_status;
      const { data } = await api.get('/superadmin/workspaces', { params });
      return data;
    },
    enabled,
  });
}

/** Suspend, reactivate, delete and rename mutations for a platform workspace. */
export function useSuperAdminWorkspaceActions() {
  const queryClient = useQueryClient();

  const suspendWorkspace = useMutation({
    mutationFn: async ({ wsId, reason }: { wsId: string; reason?: string }) => {
      const { data } = await api.post(`/superadmin/workspaces/${wsId}/suspend`, { reason });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['current-workspace'] });
    },
  });

  const reactivateWorkspace = useMutation({
    mutationFn: async (wsId: string) => {
      const { data } = await api.post(`/superadmin/workspaces/${wsId}/reactivate`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['current-workspace'] });
    },
  });

  const deleteWorkspace = useMutation({
    mutationFn: async ({ wsId, confirm = false }: { wsId: string; confirm?: boolean }) => {
      const params = confirm ? '?confirm=true' : '';
      const { data } = await api.delete(`/superadmin/workspaces/${wsId}${params}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['current-workspace'] });
      queryClient.invalidateQueries({ queryKey: ['action-center'] });
    },
  });

  const renameWorkspace = useMutation({
    mutationFn: async ({ wsId, name, slug }: { wsId: string; name: string; slug?: string }) => {
      const { data } = await api.patch<Workspace>(`/superadmin/workspaces/${wsId}/rename`, { name, slug });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  return { suspendWorkspace, reactivateWorkspace, deleteWorkspace, renameWorkspace };
}

export function useSuperAdminBroadcasts() {
  return useQuery<Broadcast[]>({
    queryKey: ['superadmin', 'broadcasts'],
    queryFn: async () => {
      const { data } = await api.get('/superadmin/broadcasts');
      return data;
    },
  });
}

/** Fields settable when creating a broadcast. Null is not accepted here. */
type BroadcastCreatePayload = {
  title: string;
  message: string;
  level: string;
  expires_at?: string;
  target_role?: string;
  target_workspace_id?: string | null;
  action_url?: string | null;
  action_label?: string | null;
};

/**
 * Fields settable when updating a broadcast. Every optional field also accepts
 * null, which clears it: the form sends null for an empty expiry, target
 * workspace, action URL and action label.
 */
type BroadcastUpdatePayload = Partial<
  Omit<BroadcastCreatePayload, 'target_role' | 'expires_at'>
> & {
  is_active?: boolean;
  target_role?: string | null;
  expires_at?: string | null;
};



/** Create, update and delete mutations for platform-wide broadcasts. */
export function useSuperAdminBroadcastActions() {
  const queryClient = useQueryClient();

  const createBroadcast = useMutation({
    mutationFn: async (payload: BroadcastCreatePayload) => {
      const { data } = await api.post<Broadcast>('/superadmin/broadcasts', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'broadcasts'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'active-broadcast'] });
    },
  });

  const updateBroadcast = useMutation({
    mutationFn: async ({
      broadcastId,
      payload,
    }: {
      broadcastId: string;
      payload: BroadcastUpdatePayload;
    }) => {
      const { data } = await api.patch<Broadcast>(`/superadmin/broadcasts/${broadcastId}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'broadcasts'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'active-broadcast'] });
    },
  });

  const deleteBroadcast = useMutation({
    mutationFn: async (broadcastId: string) => {
      await api.delete(`/superadmin/broadcasts/${broadcastId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'broadcasts'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'active-broadcast'] });
    },
  });

  return { createBroadcast, updateBroadcast, deleteBroadcast };
}

/** Issue a scoped impersonation token for another user. */
export function useSuperAdminImpersonate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<ImpersonateTokenResponse>(`/superadmin/impersonate/${userId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'impersonation-logs'] });
    },
  });
}
